import { getAskShortcut, useAskSidebar } from "../../chat/AskContext";
import React, { useState } from "react";
import { CircleAlert, Pause, Play, Square } from "lucide-react";
import SidebarToggleIcon from "../SidebarToggleIcon";

import { Button } from "common-tailwind-components";
import { useLogin } from "../../reducers";
import type { RecordingStatus } from "./types";
import type { RecordingOsPermissionsPayload } from "../../api/types";
import DashboardProfileDropdown from "./DashboardProfileDropdown";
import { useActionItems } from "./ActionItemsContext";
import RecordingTimer from "./RecordingTimer";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import RecordingOsPermissionsModal from "../RecordingOsPermissionsModal";
import {
  TITLE_BAR_HEIGHT,
  WINDOWS_CAPTION_WIDTH,
  WINDOW_CONTROLS_INSET,
  hasCustomTitleBar,
  isWindowsDesktopShell,
} from "../../utils/desktopShell";

type DashboardTopBarProps = {
  title: string;
  sidebarCollapsed: boolean;
  /** Window controls, rendered here while this bar owns the title-bar band. */
  titleBarControls: React.ReactNode;
  onShowCapturePrivacy: () => void;
  recordingStatus: RecordingStatus;
  elapsedSeconds: number;
  onStartRecording: () => void;
  onPauseRecording: () => void;
  onStopRecording: () => void;
  recordingPermissionsRequired: boolean;
  recordingPermissionsRecommended: boolean;
  recordingPermissionsOpen: boolean;
  onOpenRecordingPermissions: () => void;
  onCloseRecordingPermissions: () => void;
  onRecordingPermissionsStatus: (status: RecordingOsPermissionsPayload) => void;
  recordingControlsAvailable?: boolean;
  userInitial?: string;
};

const DashboardTopBar = ({
  title,
  sidebarCollapsed,
  titleBarControls,
  onShowCapturePrivacy,
  recordingStatus,
  elapsedSeconds,
  onStartRecording,
  onPauseRecording,
  onStopRecording,
  recordingPermissionsRequired,
  recordingPermissionsRecommended,
  recordingPermissionsOpen,
  onOpenRecordingPermissions,
  onCloseRecordingPermissions,
  onRecordingPermissionsStatus,
  recordingControlsAvailable = true,
  userInitial,
}: DashboardTopBarProps) => {
  const { t } = useTranslation();
  const { user } = useLogin();
  const ask = useAskSidebar();
  const askShortcut = getAskShortcut();
  const [profileOpen, setProfileOpen] = useState(false);
  // Every session waiting on the user is one thing to do.
  const { pendingReview, unanalyzed } = useActionItems();
  const toDo = pendingReview + unanalyzed;

  const fallbackInitial = (user.first_name || user.username || user.email || "U")
    .charAt(0)
    .toUpperCase();
  const resolvedInitial = userInitial ?? fallbackInitial;

  const text = {
    idle: recordingPermissionsRequired
      ? t("permission_required_for_recording")
      : t("start_recording"),
    recording: t("recording"),
    paused: t("paused"),
    stopping: t("stopping_recording"),
  }[recordingStatus];

  const colors = {
    idle: recordingPermissionsRequired
      ? "border-outline bg-muted-surface text-text-dim hover:bg-surface"
      : "border-success/40 bg-success/15 text-success",
    recording: "border-danger/40 bg-danger/15 text-danger",
    paused: "border-warning/40 bg-warning/15 text-warning",
    stopping: "border-info/40 bg-info/15 text-info",
  }[recordingStatus];

  const dotColor = {
    idle: "bg-success",
    recording: "bg-danger",
    paused: "bg-warning",
    stopping: "bg-info",
  }[recordingStatus];

  // A collapsed rail is too narrow for the window controls, so the bar takes
  // the whole top row and hosts them itself.
  const spansWindow = sidebarCollapsed;

  const showCapturePrivacy = () => {
    setProfileOpen(false);
    onShowCapturePrivacy();
  };

  return (
    <>
      <header
        className={clsx(
          // Opaque, and no shadow: nothing passes underneath it. The content
          // below is its own scroll container (see APP_SCROLL_ID), so the
          // frosted-glass treatment this bar used to need has nothing left to
          // blur — and on Windows the caption buttons are painted into its
          // right end in a flat colour, which never matched the translucency.
          "fixed right-0 top-0 z-[70] border-b border-outline-strong bg-background-alt",
          // In the desktop shell this bar is the window's title bar.
          hasCustomTitleBar && "app-drag",
        )}
        style={{
          left: spansWindow ? 0 : "var(--layout-sidebar-width)",
          height: TITLE_BAR_HEIGHT,
        }}
      >
        <div
          className="flex h-full items-center justify-between"
          style={{
            // Collapsed, this row starts with the window controls, so it opens
            // where they do — past the traffic lights on macOS.
            paddingLeft: spansWindow ? WINDOW_CONTROLS_INSET : 28,
            // Clear of the Windows caption buttons, painted into this same row.
            paddingRight: isWindowsDesktopShell ? WINDOWS_CAPTION_WIDTH : "clamp(8px, 2vw, 28px)",
          }}
        >
          <div className="flex min-w-0 items-center gap-2 sm:gap-5">
            {spansWindow && titleBarControls}
            {title && (
              <h1 className="min-w-0 truncate text-base font-semibold tracking-[-0.02em] text-text max-sm:sr-only">
                {ask.expanded && ask.watchRequest && !ask.history ? t("askPanel.build_kpi") : title}
              </h1>
            )}
            {!recordingControlsAvailable ? null : recordingStatus === "idle" ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-tour="start-recording"
                  aria-label={text}
                  onClick={
                    recordingPermissionsRequired ? onOpenRecordingPermissions : onStartRecording
                  }
                  title={recordingPermissionsRequired ? t("set_recording_permissions") : undefined}
                  className={clsx(
                    "app-no-drag flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium max-sm:p-2",
                    colors,
                  )}
                >
                  {recordingPermissionsRequired ? (
                    <CircleAlert size={14} className="text-text-dim" />
                  ) : (
                    <span className="h-2 w-2 rounded-full bg-success" />
                  )}
                  <span className="max-sm:sr-only">{text}</span>
                </button>
                {!recordingPermissionsRequired && recordingPermissionsRecommended && (
                  <button
                    type="button"
                    onClick={onOpenRecordingPermissions}
                    title={t("recording_permissions_recommended_description")}
                    aria-label={t("recording_permissions_recommended_description")}
                    className="app-no-drag flex h-7 w-7 items-center justify-center rounded-full border border-warning/40 bg-warning/10 text-warning-strong hover:bg-warning/20"
                  >
                    <CircleAlert size={14} />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div
                  className={clsx(
                    "flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium",
                    colors,
                  )}
                >
                  <span className={`h-2 w-2 animate-pulse rounded-full ${dotColor}`} />
                  {text}
                  {" - "}
                  <RecordingTimer seconds={elapsedSeconds} />
                </div>
                {recordingStatus !== "stopping" && (
                  <div className="app-no-drag flex items-center gap-2">
                    <Button color="warning" type="button" onClick={onPauseRecording}>
                      {recordingStatus === "paused" ? <Play /> : <Pause />}
                    </Button>
                    <Button color="danger" type="button" onClick={onStopRecording}>
                      <Square />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* data-profile-menu marks the avatar and the menu as one unit for
              the click-away dismissal in DashboardProfileDropdown. */}
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
            <button
              type="button"
              onClick={ask.toggle}
              aria-expanded={ask.open}
              aria-controls="ask-sidebar"
              aria-keyshortcuts={askShortcut.ariaKeyShortcuts}
              title={`${t(ask.open ? "chat_close_sidebar" : "chat_opens_sidebar")} (${askShortcut.label})`}
              className={clsx(
                "app-no-drag inline-flex items-center gap-2 rounded-lg border border-transparent px-3 py-1.5 text-sm font-medium text-text transition-colors hover:bg-muted-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                ask.open ? "bg-muted-surface" : "bg-transparent",
              )}
            >
              <SidebarToggleIcon collapsed={!ask.open} side="right" />
              {t("dashboard.ask")}
            </button>
            <div className="relative" data-profile-menu="">
              <button
                type="button"
                onClick={() => setProfileOpen(prev => !prev)}
                className="app-no-drag relative flex h-8 w-8 items-center justify-center rounded-full bg-accent text-sm font-semibold text-on-accent"
                title={t("user")}
              >
                {resolvedInitial}
                {toDo > 0 && (
                  <>
                    <span className="sr-only">{t("activity.to_do_count", { count: toDo })}</span>
                    <span
                      aria-hidden="true"
                      className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-unread px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-background-alt"
                    >
                      {toDo > 99 ? "99+" : toDo}
                    </span>
                  </>
                )}
              </button>

              {profileOpen && (
                <DashboardProfileDropdown
                  onClose={() => setProfileOpen(false)}
                  onShowCapturePrivacy={showCapturePrivacy}
                  showCapturePrivacy={recordingControlsAvailable}
                />
              )}
            </div>
          </div>
        </div>
      </header>
      {recordingControlsAvailable && (
        <RecordingOsPermissionsModal
          open={recordingPermissionsOpen}
          onClose={onCloseRecordingPermissions}
          onShowCapturePrivacy={onShowCapturePrivacy}
          onStatus={onRecordingPermissionsStatus}
        />
      )}
    </>
  );
};

export default DashboardTopBar;
